import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, MessageSquare, ChevronLeft, AlertTriangle, Compass } from "lucide-react";
import { submitFeedback } from "@/lib/feedback";
import { softDeleteOwnAccount } from "@/lib/account";

const CATEGORIES = [
  { key: "bug",     label: "Bug report" },
  { key: "feature", label: "Feature request" },
  { key: "general", label: "General feedback" },
];

export default function SettingsPage() {
  const { user, profile, signOut, openOnboarding } = useAuth();
  const navigate = useNavigate();

  // Feedback form
  const [fb, setFb] = useState({ category: "general", subject: "", message: "" });
  const [fbBusy, setFbBusy] = useState(false);

  // Delete account
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const sendFeedback = async (e) => {
    e.preventDefault();
    if (fb.subject.trim().length < 3) { toast.error("Subject needs at least 3 characters."); return; }
    if (fb.message.trim().length < 5) { toast.error("Tell us a bit more in the message."); return; }
    setFbBusy(true);
    try {
      await submitFeedback({
        user_id: user?.id,
        category: fb.category,
        subject: fb.subject,
        message: fb.message,
      });
      toast.success("Feedback sent. Thank you.");
      setFb({ category: "general", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.message || "Couldn't send feedback.");
    }
    setFbBusy(false);
  };

  const onDelete = async () => {
    if (confirmText.trim().toLowerCase() !== "delete") {
      toast.error('Type "delete" to confirm.');
      return;
    }
    setDelBusy(true);
    try {
      await softDeleteOwnAccount(user?.id);
      toast.success("Your account has been deleted.");
      try { await signOut?.(); } catch { /* already signed out */ }
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Couldn't delete the account.");
      setDelBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-8 md:py-12">
      <Link to="/profile" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink mb-6">
        <ChevronLeft size={14} strokeWidth={2.8} /> Back to profile
      </Link>

      <span className="text-xs uppercase tracking-[0.3em] text-ink/60">Settings</span>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2 mb-8 leading-none">
        Account &<br />feedback.
      </h1>

      {/* Feedback */}
      <section data-testid="settings-feedback-section" className="border-[2.5px] border-ink rounded-3xl p-6 md:p-8 bg-paper">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare size={18} strokeWidth={2.6} />
          <span className="text-xs uppercase tracking-[0.3em] font-extrabold">Feedback &amp; suggestions</span>
        </div>
        <p className="text-sm text-ink/65 mb-5">Found a bug? Got an idea? Tell us anything.</p>

        <form onSubmit={sendFeedback} className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = fb.category === c.key;
              return (
                <button
                  type="button"
                  key={c.key}
                  onClick={() => setFb({ ...fb, category: c.key })}
                  data-testid={`fb-cat-${c.key}`}
                  className={`px-4 py-2 rounded-full border-[2.5px] border-ink text-xs uppercase tracking-widest font-extrabold transition-colors ${active ? "bg-neon" : "bg-paper hover:bg-ink hover:text-paper"}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <input
            type="text" required maxLength={140}
            value={fb.subject}
            onChange={(e) => setFb({ ...fb, subject: e.target.value })}
            placeholder="Subject"
            data-testid="fb-subject"
            className="w-full border-[2.5px] border-ink rounded-2xl bg-paper px-4 py-3 text-base font-medium outline-none"
          />
          <textarea
            required maxLength={4000} rows={5}
            value={fb.message}
            onChange={(e) => setFb({ ...fb, message: e.target.value })}
            placeholder="What's on your mind?"
            data-testid="fb-message"
            className="w-full border-[2.5px] border-ink rounded-2xl bg-paper px-4 py-3 text-base font-medium outline-none resize-y"
          />
          <button
            type="submit"
            disabled={fbBusy}
            data-testid="fb-submit"
            className="self-start bg-neon border-[2.5px] border-ink rounded-full px-6 py-3 text-xs uppercase tracking-widest font-extrabold shadow-pop-sm hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform disabled:opacity-60"
          >
            {fbBusy ? "Sending…" : "Send feedback"}
          </button>
        </form>
      </section>

      {/* App tour */}
      <section data-testid="settings-tour-section" className="border-[2.5px] border-ink rounded-3xl p-6 md:p-8 mt-8 bg-paper">
        <div className="flex items-center gap-2 mb-1">
          <Compass size={18} strokeWidth={2.6} />
          <span className="text-xs uppercase tracking-[0.3em] font-extrabold">App tour</span>
        </div>
        <p className="text-sm text-ink/70 mb-5">
          Refresh on how inonit works — discovery, joining, and hosting in under a minute.
        </p>
        <button
          type="button"
          onClick={openOnboarding}
          data-testid="settings-open-onboarding"
          className="inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full px-5 py-3 bg-paper hover:bg-lime text-ink font-extrabold text-xs uppercase tracking-widest transition-colors"
        >
          <Compass size={14} strokeWidth={2.8} /> View onboarding again
        </button>
      </section>

      {/* Delete account */}
      <section data-testid="settings-delete-section" className="border-[2.5px] border-destructive/60 rounded-3xl p-6 md:p-8 mt-8 bg-destructive/5">
        <div className="flex items-center gap-2 mb-1 text-destructive">
          <AlertTriangle size={18} strokeWidth={2.6} />
          <span className="text-xs uppercase tracking-[0.3em] font-extrabold">Danger zone</span>
        </div>
        <p className="text-sm text-ink/70 mb-5">
          Delete your inonit account. Your hosted events stay archived for analytics
          but no longer appear publicly. Your handle will be freed up. This can't be undone.
        </p>
        {!confirmingDelete ? (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            data-testid="delete-account-start"
            className="inline-flex items-center gap-2 border-[2.5px] border-destructive text-destructive rounded-full px-5 py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-destructive hover:text-paper transition-colors"
          >
            <Trash2 size={14} strokeWidth={2.6} /> Delete my account
          </button>
        ) : (
          <div className="flex flex-col gap-3" data-testid="delete-account-confirm">
            <p className="text-sm text-ink">
              Type <span className="font-extrabold">delete</span> below to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="delete"
              data-testid="delete-account-input"
              className="w-full max-w-xs border-[2.5px] border-destructive rounded-2xl bg-paper px-4 py-3 text-base font-medium outline-none"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onDelete}
                disabled={delBusy}
                data-testid="delete-account-confirm-btn"
                className="bg-destructive text-paper border-[2.5px] border-destructive rounded-full px-5 py-3 text-xs uppercase tracking-widest font-extrabold disabled:opacity-60"
              >
                {delBusy ? "Deleting…" : "Yes, delete forever"}
              </button>
              <button
                type="button"
                onClick={() => { setConfirmingDelete(false); setConfirmText(""); }}
                disabled={delBusy}
                className="border-[2.5px] border-ink rounded-full px-5 py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-ink hover:text-paper transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {profile?.username && (
          <p className="text-[11px] text-ink/45 mt-4 uppercase tracking-widest">
            Signed in as @{profile.username}
          </p>
        )}
      </section>
    </div>
  );
}
